class NotificationService {
    private static instance: NotificationService;
    private audio: HTMLAudioElement | null = null;
    private _enabled: boolean = false;

    // Listeners for UI state updates
    private listeners: Set<(enabled: boolean) => void> = new Set();

    private constructor() {
        this.init();
    }

    static getInstance(): NotificationService {
        if (!NotificationService.instance) {
            NotificationService.instance = new NotificationService();
        }
        return NotificationService.instance;
    }

    private get isSupported(): boolean {
        return 'Notification' in window;
    }

    private get isPermissionGranted(): boolean {
        return this.isSupported && Notification.permission === 'granted';
    }

    get enabled(): boolean {
        return this._enabled;
    }

    private init() {
        // Restore saved preference
        this._enabled = localStorage.getItem('alertsEnabled') === 'true';

        if (!this.isSupported) {
            console.warn('[NotificationService] This browser does not support desktop notifications');
        }

        console.log('[NotificationService] Initialized. Enabled:', this._enabled, '| Permission:', this.isSupported ? Notification.permission : 'not supported');

        // Use local notification sound
        this.audio = new Audio('/notification.mp3');
    }

    // Subscribe to enable/disable state changes
    onStateChange(callback: (enabled: boolean) => void) {
        this.listeners.add(callback);
        return () => { this.listeners.delete(callback); };
    }

    private notifyListeners() {
        this.listeners.forEach(cb => cb(this._enabled));
    }

    async enable(): Promise<boolean> {
        // Request notification permission if not yet granted
        if (this.isSupported && Notification.permission === 'default') {
            const permission = await Notification.requestPermission();
            console.log('[NotificationService] User responded with:', permission);
        }

        this._enabled = true;
        localStorage.setItem('alertsEnabled', 'true');
        this.notifyListeners();

        // Play sound once to unlock browser audio autoplay and confirm it works
        this.playAlertSound();

        console.log('[NotificationService] Alerts ENABLED');
        return true;
    }

    disable() {
        this._enabled = false;
        localStorage.setItem('alertsEnabled', 'false');
        this.notifyListeners();
        console.log('[NotificationService] Alerts DISABLED');
    }

    async requestPermission(): Promise<boolean> {
        if (!this.isSupported) return false;

        if (Notification.permission === 'granted') {
            return true;
        }

        if (Notification.permission === 'denied') {
            console.warn('[NotificationService] Permission denied. User must enable from browser site settings.');
            return false;
        }

        const permission = await Notification.requestPermission();
        return permission === 'granted';
    }

    playAlertSound() {
        if (this.audio) {
            this.audio.currentTime = 0;
            this.audio.play().catch(e => console.log('Audio play prevented by browser policy until interaction', e));
        }
    }

    notifyNewOrder(orderId: string) {
        // If alerts are disabled, skip everything
        if (!this._enabled) {
            console.log('[NotificationService] Alerts disabled, skipping notification for order:', orderId);
            return;
        }

        const title = '🔔 New Order Received!';
        const body = `Order #${orderId.slice(0, 8)} has just been placed.`;

        console.log('[NotificationService] New order alert:', orderId, '| Permission:', this.isSupported ? Notification.permission : 'not supported');

        // Play Sound
        this.playAlertSound();

        // Show Native Browser Notification
        if (this.isPermissionGranted) {
            try {
                const options: NotificationOptions = {
                    body,
                    icon: '/vite.svg',
                    tag: `order-${orderId}`,
                    requireInteraction: true,
                    silent: true, // Suppress browser's default beep — we play our own sound
                };

                // Try to use Service Worker to show notification (Required for Android/PWA)
                if ('serviceWorker' in navigator) {
                    navigator.serviceWorker.ready.then((registration) => {
                        registration.showNotification(title, options);
                        console.log('[NotificationService] Notification shown via Service Worker');
                        // No need for onclick window.focus here as SW handles clicks differently,
                        // but PWA will surface the notification natively on Android.
                    }).catch(() => {
                        // Fallback if SW fails
                        this.showStandardNotification(title, options);
                    });
                } else {
                    // Standard fallback
                    this.showStandardNotification(title, options);
                }
            } catch (e) {
                console.error('[NotificationService] Failed to show notification:', e);
            }
        } else {
            console.log('[NotificationService] Cannot show native notification (permission denied)');
        }
    }

    private showStandardNotification(title: string, options: NotificationOptions) {
        try {
            const notification = new Notification(title, options);
            setTimeout(() => notification.close(), 15000);
            notification.onclick = () => {
                window.focus();
                notification.close();
            };
            console.log('[NotificationService] Browser notification shown successfully');
        } catch (e) {
            console.error('Failed to show standard notification:', e);
        }
    }
}
export const notificationService = NotificationService.getInstance();
